import { useEffect, useMemo, useState } from "react";
import { getSessionPath } from "../lib/apiPaths";
import { apiUrl } from "../lib/tenantOrigin";

type SessionUser = {
  id: string;
  email: string;
  tenantId: string;
} | null;

type AuthSession = {
  session: { id: string; userId: string };
  user: { id: string; email: string };
  // The stack's single tenants.id, carried alongside the session so clients can
  // stamp tenant_id on optimistic mutations before Zero has synced.
  tenantId: string;
  intercomUserHash?: string;
} | null;

// The server-computed Intercom identity-verification hash, captured from the
// bootstrap fetch so the messenger doesn't need a second get-session call.
// Set before the signed-in tree (and thus the messenger) ever mounts.
let sessionIntercomUserHash: string | undefined;

export function intercomUserHash(): string | undefined {
  return sessionIntercomUserHash;
}

export function useSessionBootstrap() {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [user, setUser] = useState<SessionUser>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Retry with backoff rather than getting stuck on the loading screen
      // or treating a network blip as signed-out. The endpoint answers a
      // signed-out user with 200 and a null body, so any 5xx is a transient
      // infra failure (e.g. a gateway mid-deploy) that we retry, not sign-out.
      let delay = 500;
      while (!cancelled) {
        try {
          const response = await fetch(`${apiUrl()}${getSessionPath()}`, {
            credentials: "include",
          });
          if (response.status >= 500) throw new Error(String(response.status));
          const session = response.ok
            ? ((await response.json()) as AuthSession)
            : null;
          if (cancelled) return;
          sessionIntercomUserHash = session?.intercomUserHash;
          if (session?.user && session.tenantId) {
            setUser({
              ...session.user,
              tenantId: session.tenantId,
            });
          } else {
            setUser(null);
          }
          setHasLoaded(true);
          return;
        } catch {
          await new Promise((resolve) => setTimeout(resolve, delay));
          delay = Math.min(delay * 2, 10_000);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => ({ user, hasLoaded }), [user, hasLoaded]);
}
